import React, { useEffect, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import AreaCodeSelector from '@/ui/AreaCodeSelector';
import { Style } from '@/components/style';
import Validator from '@/utils/validator';
import Area from '@/utils/area';
import { FormattedMessage, useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import '@/components/member/member.scss';
import PrimaryButton from '@/ui/PrimaryButton';
import {
  setCurrentSelectedPhone,
  setMemberPageState,
} from '@/actions/member/memberAction';
import { ReactComponent as Warn } from '@/assets/img/warn.svg';
import { useLocation } from 'react-router-dom';
import * as MemberInfoAction from '@/actions/memberInfo/memberInfoAction';
import { setMemberInfoPhone } from '@/actions/memberInfo/memberInfoAction';
import MemberHeader from '@components/member/components/memberHeader';
import PropTypes from 'prop-types';
import {
  setWelcomeCustomerName,
  setWelcomeCustomerPhone,
} from '@actions/customizeWelcome/customizeWelcomeAction';
import {
  setCartCustomerName,
  setCartCustomerPhone,
} from '@actions/cartV2/cartV2Action';

const { alertColor, blackColor, gray3Color } = new Style().getUiConfig();

function MemberLogin({ storeUuid }) {
  const intl = useIntl();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const dispatch = useDispatch<any>();
  const [areaCodes, setAreaCodes] = useState<any>(null);
  const [selectedAreaCode, setSelectedAreaCode] = useState<any>(null);
  const [isPhoneValid, setPhoneValid] = useState(true);
  const [phone, setPhone] = useState<string>('');

  const memberLangs = useSelector(
    (state: RootStateOrAny) => state.memberReducer.memberLangs
  );
  const memberInfo = useSelector(
    (state: RootStateOrAny) => state.memberInfoReducer
  );
  const memberPageState = useSelector(
    (state: RootStateOrAny) => state.memberReducer.memberPageState
  );
  const redirectToken = queryParams.get('redirectToken') || '';

  useEffect(() => {
    if (memberPageState !== 'MemberLogin') return;

    if (isEmpty(memberInfo?.areaCode) || isEmpty(memberInfo?.phone)) return;
    setSelectedAreaCode(memberInfo.areaCode);
    setPhone(memberInfo?.phone);
  }, [memberPageState]);

  useEffect(() => {
    if (isEmpty(memberLangs)) return;

    const tempAreaCodes = Area.getAreaCodesListByLangs(memberLangs);
    setAreaCodes(tempAreaCodes);
    setSelectedAreaCode(tempAreaCodes[0]);
  }, [memberLangs]);

  useEffect(() => {
    if (isEmpty(phone)) return;
    setPhoneValid(Validator.phoneValidator(phone, 'TW', true)); // TODO : 不同國家的檢查
  }, [phone]);

  const onChangePhone = (e) => {
    setPhone(e.target.value);
  };

  const submit = async () => {
    // check is phone valid
    if (isEmpty(phone)) {
      setPhoneValid(false);
      return;
    }
    if (!isPhoneValid) return;

    const checkPhoneVo = await dispatch(
      MemberInfoAction.checkPhoneAndSetIsFullMember(
        phone,
        selectedAreaCode.country,
        storeUuid,
        redirectToken
      )
    );

    const selectedMemberPhone = { areaCode: selectedAreaCode, phone };
    dispatch(setCurrentSelectedPhone(selectedMemberPhone));
    dispatch(setMemberInfoPhone(selectedMemberPhone));

    if (checkPhoneVo.phoneFullMember) {
      dispatch(setWelcomeCustomerName(checkPhoneVo.memberName));
      dispatch(setWelcomeCustomerPhone(phone));

      dispatch(setCartCustomerName(checkPhoneVo.memberName));
      dispatch(setCartCustomerPhone(phone));

      const allElements: any = document.getElementsByTagName('*');
      for (let i = 0; i < allElements.length; i++) {
        if (allElements[i].id == 'phone') allElements[i].disabled = true;
        if (allElements[i].id == 'areaCodeDropDownMenu')
          allElements[i].disabled = true;
      }

      dispatch(setMemberPageState(''));
    } else {
      // 未註冊 不可登入 跳至註冊頁
      dispatch(setMemberPageState('MemberRegister'));
    }
  };

  const getPhoneErrorFormatMessage = () => {
    if (isEmpty(phone)) {
      return intl.formatMessage({
        id: 'member.profile.phone.empty',
      });
    }
    return intl.formatMessage({
      id: 'member.profile.phone.format.warn',
    });
  };

  return (
    <>
      <MemberHeader
        title={intl.formatMessage({
          id: 'tel.placeholder',
        })}
      />
      <div className="memberLayout">
        <div className="d-flex flex-column mb-5">
          <div className="gap-2 d-flex justify-content-between">
            <AreaCodeSelector
              setAreaCode={setSelectedAreaCode}
              areaCode={selectedAreaCode}
              areaCodeList={areaCodes}
            />

            <input
              className="phone"
              style={{
                border: `1px solid ${isPhoneValid ? gray3Color : gray3Color}`,
                color: `${isPhoneValid ? blackColor : alertColor}`,
                padding: '10px',
              }}
              type="tel"
              id="phone"
              data-testid="phone"
              placeholder={intl.formatMessage({
                id: 'cart.customerInfo.phone.required',
              })}
              value={phone}
              onChange={onChangePhone}
            />
          </div>
          <div style={{ height: '1em' }}>
            {!isPhoneValid && (
              <div
                className="d-flex py-1 gap-1 align-items-center"
                style={{ color: alertColor, fontSize: '12px' }}
              >
                <Warn style={{ marginRight: '5px' }} />
                {getPhoneErrorFormatMessage()}
              </div>
            )}
          </div>
        </div>
        <div className="d-flex justify-content-center">
          <PrimaryButton
            onClick={submit}
            disabled={!isPhoneValid || isEmpty(phone)}
            id="member_login"
          >
            <div className="fw-bolder" style={{ fontSize: '18px' }}>
              <FormattedMessage id="member.common.btn.login" />
            </div>
          </PrimaryButton>
        </div>
      </div>
    </>
  );
}

MemberLogin.defaultProps = {};

MemberLogin.propTypes = {
  storeUuid: PropTypes.string.isRequired,
};

export default MemberLogin;
